import { useState, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import { toast } from "../components/toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addRealtimeNotification } from "../slice/notifications/notificationsSlice";
import { getUserNotifications } from "../reducer/notifications/notificationsReducer";
import notificationSound from "../assets/notification/alert1.mp3";
import {
  isChatOpen,
  OPEN_CHAT_MESSAGE_EVENT,
} from "../utils/activeChatRegistry";
import {
  ensureFreshAccessToken,
  getAccessToken,
  getTokenUserId,
  isAuthError,
} from "../utils/socketAuth";
import {
  CHAT_SOCKET_CONNECTED_EVENT,
  isAdminSupportMessage,
} from "../utils/chatEvents";

const SOCKET_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// GLOBAL duplicate prevention - shared across all hook instances
const globalShownNotificationIds = new Set();

// ---------------------------------------------------------------------------------------------
// ONE notification socket per browser tab, shared by every mounted dashboard layout.
//
// Every page renders its own layout, so a socket owned by the hook was torn down and
// re-handshaken on each navigation: messages emitted during that gap never arrived, and a socket
// still connecting at unmount leaked with its listeners. The socket now lives at module level;
// the hook only takes and releases a reference, and the socket is closed a few seconds after the
// last layout unmounts (logout, or leaving the dashboard), not on every page change.
// ---------------------------------------------------------------------------------------------
const RELEASE_DELAY_MS = 3000;
const MAX_RETRY_DELAY_MS = 30000;
const shared = {
  socket: null,
  userId: null,
  users: 0,
  releaseTimer: null,
  retryTimer: null,
  retries: 0,
};
// Latest React context for the socket handlers, which outlive any single layout mount.
const ctx = { dispatch: null, navigate: null };

const playNotificationSound = () => {
  try {
    const audio = new Audio(notificationSound);
    audio.volume = 0.5;
    audio
      .play()
      .catch((err) =>
        console.warn("[SOCKET_CLIENT] Could not play notification sound:", err),
      );
  } catch (err) {
    console.warn("[SOCKET_CLIENT] Error creating notification audio:", err);
  }
};

const isChatNotification = (n) =>
  n?.notification_type === "CHAT_MESSAGE" ||
  (n?.title && String(n.title).toLowerCase() === "new message");

const handleNotification = (notificationData) => {
  console.log(
    "[SOCKET_CLIENT] Received notification:",
    notificationData?.notification_type,
    notificationData?.notification_id || notificationData?.id,
  );

  // Check if this notification was already shown (prevent duplicate toasts)
  const notificationId =
    notificationData.notification_id || notificationData.id;
  if (globalShownNotificationIds.has(notificationId)) return;
  if (notificationId) {
    globalShownNotificationIds.add(notificationId);
    // Clean up old notification IDs after 1 minute to prevent memory leak
    setTimeout(() => globalShownNotificationIds.delete(notificationId), 60000);
  }

  ctx.dispatch?.(addRealtimeNotification(notificationData));

  // Emit custom event for silent page refresh
  window.dispatchEvent(
    new CustomEvent("imaging_notification_received", {
      detail: notificationData,
    }),
  );

  // Settlement (withdrawal) decision notifications — refresh the withdrawal page in real time.
  const settlementNotificationTypes = [
    "SETTLEMENT_APPROVED",
    "SETTLEMENT_REJECTED",
    "SETTLEMENT_COMPLETED",
  ];
  if (
    settlementNotificationTypes.includes(notificationData.notification_type)
  ) {
    window.dispatchEvent(
      new CustomEvent("settlement_updated", { detail: notificationData }),
    );
  }

  // Chat messages are announced by their `new_message` event: patient chats by the handler
  // below (toast + dock), admin support chats by the sidebar Chat badge. The CHAT_MESSAGE
  // notification only lands in the bell; a second sound/toast here labelled every patient
  // message as "from Admin".
  if (isChatNotification(notificationData)) return;

  playNotificationSound();
  toast.info(
    notificationData.body || notificationData.message || "New notification",
    {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      onClick: () => {
        const bookingId =
          notificationData.data?.booking_id || notificationData.data?.order_id;
        if (bookingId && ctx.navigate) {
          ctx.navigate(`/imaging_appointment_detail/${bookingId}`);
        }
      },
    },
  );
};

// Appointment chat: a patient (or other party) sent a message. The notification socket lives in
// the user's personal room, so it also receives `new_message` app-wide. Show a toast (unless the
// user is already viewing that chat) and refresh the per-appointment unread badge on chat icons.
const handleNewMessage = (data) => {
  try {
    if (Number(data?.sender_id) === Number(getTokenUserId())) return; // ignore our own messages

    // Admin/support chat (room_context "admin_provider" = this provider's support room with the
    // admin team, whichever admin wrote) is surfaced by the sidebar Chat badge — NOT the
    // appointment ChatDock. Only center↔patient chats open a docked window.
    if (isAdminSupportMessage(data)) return;

    // The provider already has this conversation open (page-level chat or an expanded dock
    // bubble): hand the message to that chat window and skip the sound, the toast and the dock
    // bubble. The open chat de-duplicates it against its own socket and marks the room read.
    if (
      isChatOpen({
        roomId: data?.room_id,
        appointmentId: data?.appointment_id,
        senderId: data?.sender_id,
      })
    ) {
      window.dispatchEvent(
        new CustomEvent(OPEN_CHAT_MESSAGE_EVENT, { detail: data }),
      );
      return;
    }

    // Live-refresh the unread badge on chat icons.
    window.dispatchEvent(
      new CustomEvent("chat-unread-refresh", {
        detail: {
          room_id: data?.room_id,
          appointment_id: data?.appointment_id,
        },
      }),
    );

    playNotificationSound();
    const isArabic = (localStorage.getItem("i18nextLng") || "")
      .toLowerCase()
      .startsWith("ar");
    const senderName = data?.sender_name || (isArabic ? "مريض" : "a patient");
    const apptId = data?.appointment_id;
    const msg = isArabic
      ? `📩 رسالة جديدة من ${senderName}${apptId ? ` (موعد رقم ${apptId})` : ""}`
      : `📩 New message from ${senderName}${apptId ? ` (Appointment #${apptId})` : ""}`;
    toast.info(msg, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
    });

    // Open a minimized appointment-chat dock (bottom-right) for this message so the provider
    // can jump straight into the conversation. ChatDock listens for this event.
    window.dispatchEvent(
      new CustomEvent("chat-incoming-message", {
        detail: {
          room_id: data?.room_id,
          appointment_id: data?.appointment_id,
          sender_id: data?.sender_id,
          sender_name: data?.sender_name,
        },
      }),
    );
  } catch (e) {}
};

// Reconnect a socket that socket.io will not reconnect by itself: the gateway refused the
// handshake (expired JWT) or closed the connection. Refreshes the token first when needed.
const scheduleManualReconnect = (socket, { authRejected = false } = {}) => {
  if (socket !== shared.socket || shared.retryTimer) return;
  const delay = Math.min(MAX_RETRY_DELAY_MS, 1000 * 2 ** shared.retries);
  shared.retries += 1;
  shared.retryTimer = setTimeout(async () => {
    shared.retryTimer = null;
    if (socket !== shared.socket || socket.connected) return;
    const rejected = authRejected ? socket.io.opts.query?.token : null;
    const token = await ensureFreshAccessToken(rejected);
    // No session any more (logout) or another user signed in: stop here.
    if (!token || socket !== shared.socket) return;
    if (String(getTokenUserId(token)) !== String(shared.userId)) return;
    socket.io.opts.query = { token };
    socket.connect();
  }, delay);
};

const createSocket = (token) => {
  console.log("[SOCKET_CLIENT] Initializing shared notification socket...");
  const socket = io(SOCKET_URL, {
    query: { token },
    transports: ["websocket", "polling"],
    reconnection: true,
    // Never give up on transport failures: a dead notification socket silently stops chats,
    // badges and the dock until the next full page load.
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    forceNew: true,
  });

  socket.on("connect", () => {
    console.log("[SOCKET_CLIENT] Socket connected:", socket.id);
    shared.retries = 0;
    // Anything may have changed while the socket was down: let unread badges re-sync.
    window.dispatchEvent(new CustomEvent(CHAT_SOCKET_CONNECTED_EVENT));
    window.dispatchEvent(
      new CustomEvent("chat-unread-refresh", { detail: {} }),
    );
  });

  socket.on("disconnect", (reason) => {
    console.log("[SOCKET_CLIENT] Socket disconnected:", reason);
    // Server-side close: socket.io does not reconnect by itself.
    if (reason === "io server disconnect") scheduleManualReconnect(socket);
  });

  socket.on("connect_error", (error) => {
    console.error(
      "[SOCKET_CLIENT] Socket connection error:",
      error?.message || error,
    );
    // `active` is false when the gateway refused the handshake (auth middleware); socket.io
    // then waits for a manual connect(). Transport errors keep it active and retry by themselves.
    if (!socket.active) {
      scheduleManualReconnect(socket, { authRejected: isAuthError(error) });
    }
  });

  // Each automatic reconnect re-sends the handshake: use the newest stored token, not the one
  // the socket was created with (the app refreshes it in the background).
  socket.io.on("reconnect_attempt", () => {
    const latest = getAccessToken();
    if (latest) socket.io.opts.query = { token: latest };
  });

  socket.on("notification", handleNotification);
  socket.on("new_message", handleNewMessage);

  socket.on("connected", (data) => {
    console.log("[SOCKET_CLIENT] Server confirmed connection:", data?.user_id);
  });

  socket.on("error", (error) => {
    console.error("[SOCKET_CLIENT] Socket error:", error);
  });

  return socket;
};

export const disposeSharedSocket = () => {
  if (shared.releaseTimer) clearTimeout(shared.releaseTimer);
  if (shared.retryTimer) clearTimeout(shared.retryTimer);
  shared.releaseTimer = null;
  shared.retryTimer = null;
  const socket = shared.socket;
  shared.socket = null;
  shared.userId = null;
  shared.retries = 0;
  if (socket) {
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (e) {}
  }
};

const acquireSharedSocket = () => {
  const token = getAccessToken();
  if (!token) return null;
  const userId = getTokenUserId(token);
  if (shared.releaseTimer) {
    clearTimeout(shared.releaseTimer);
    shared.releaseTimer = null;
  }
  // Another account signed in on this tab: never reuse the previous user's socket.
  if (shared.socket && String(shared.userId) !== String(userId)) {
    disposeSharedSocket();
  }
  if (!shared.socket) {
    shared.socket = createSocket(token);
    shared.userId = userId;
  } else if (!shared.socket.connected && !shared.socket.active) {
    // Refused earlier and not retrying: try again with the current token.
    shared.socket.io.opts.query = { token };
    shared.socket.connect();
  }
  shared.users += 1;
  return shared.socket;
};

const releaseSharedSocket = () => {
  shared.users = Math.max(0, shared.users - 1);
  if (shared.users > 0 || shared.releaseTimer) return;
  // A navigation unmounts one page's layout and mounts the next a moment later: keep the
  // connection across that hand-over and close it only if no layout comes back.
  shared.releaseTimer = setTimeout(() => {
    shared.releaseTimer = null;
    if (shared.users === 0) disposeSharedSocket();
  }, RELEASE_DELAY_MS);
};

export const useSocketNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications: reduxNotifications } = useSelector(
    (state) => state.notifications,
  );
  const [socket, setSocket] = useState(() => shared.socket);
  const [isConnected, setIsConnected] = useState(
    () => !!shared.socket?.connected,
  );

  useEffect(() => {
    ctx.dispatch = dispatch;
    ctx.navigate = navigate;
  }, [dispatch, navigate]);

  useEffect(() => {
    const s = acquireSharedSocket();
    if (!s) {
      console.warn("No JWT token found for socket connection");
      return undefined;
    }
    setSocket(s);
    setIsConnected(s.connected);
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      releaseSharedSocket();
    };
  }, []);

  // Load notifications from API on mount
  useEffect(() => {
    const token = getAccessToken();
    if (token && !reduxNotifications.length) {
      // Load initial notifications from API
      dispatch(getUserNotifications({ page: 1, limit: 20 }));
    }
  }, [dispatch, reduxNotifications.length]);

  const connectSocket = useCallback(() => {
    const s = shared.socket;
    if (s && !s.connected) s.connect();
  }, []);

  const disconnectSocket = useCallback(() => {
    disposeSharedSocket();
    setSocket(null);
    setIsConnected(false);
  }, []);

  // Notifications are managed by Redux; kept for API compatibility.
  const clearNotifications = useCallback(() => {}, []);
  const removeNotification = useCallback(() => {}, []);
  const markAsRead = useCallback(() => {}, []);

  return {
    socket,
    isConnected,
    notifications: reduxNotifications,
    connectSocket,
    disconnectSocket,
    clearNotifications,
    removeNotification,
    markAsRead,
  };
};

export default useSocketNotifications;
